import { INJECTION_COLUMN } from '../../../../../../../../utils/enums';
import AntAutoCompleteV2 from '../../../AntAutoComplete';
import { ALL_FORM_UNITS_FOR_MX, EditableCellV2 } from '../../../MedTableRenderer';
import AddItemChip from '../../../../../../../common/AddItemChip';
import {
    FlatInjectionObj,
    InjectionsEntity,
} from '../../../../../../../../store/types/prescription';
import { injectionRowHeaderMapping } from '../../../../../../../../constants';
import { INJECTIONS_TABLE_CONTAINER } from '../InjectionTable';
import { useDispatch, useSelector } from 'react-redux';
import { getInjectionElements } from '../../../../../../../../store/selectors/prescriptionPad';
import {
    setInjectionElements,
    upsertInjection,
} from '../../../../../../../../store/actions/prescriptionPad';
import MX_DOSAGE_FORM_UNIT, { FORM_TO_DF_MAPPING } from '../../../../mxProperties';
import { generateDoseOptions } from '../../../../ExpandedMedCard';

const InjectionDose = ({
    injection,
    columnKey,
    isDragMode,
}: {
    injection: FlatInjectionObj;
    columnKey: INJECTION_COLUMN;
    isDragMode: boolean;
}): JSX.Element => {
    const elements = useSelector(getInjectionElements);
    const dispatch = useDispatch();

    const showAddTaperingStrip =
        (injection.is_last_drug && injection.is_last_tapering) ||
        (elements[injection.mainIndex]?.tapering_dose &&
            elements[injection.mainIndex]?.tapering_dose?.length &&
            injection.is_last_tapering);

    const currentElement = injection.is_added_drug
        ? elements[injection.mainIndex]?.added_drug?.[injection.currentIndex]
        : elements[injection.mainIndex];

    const onDoseSelect = (
        option?: { id: string; label: string; value: string; meta: any } | undefined,
    ) => {
        const { is_added_drug, is_tapering_dose, currentIndex, mainIndex } = injection;
        const clonedElement = elements[mainIndex];
        if (is_added_drug) {
            if (clonedElement.added_drug?.[currentIndex]) {
                clonedElement.added_drug[currentIndex].dose = {
                    ...(clonedElement.added_drug[currentIndex].dose || {}),
                    custom: option?.label,
                };
            }
        } else if (is_tapering_dose) {
            if (clonedElement.tapering_dose?.[currentIndex]) {
                clonedElement.tapering_dose[currentIndex].dose = {
                    ...(clonedElement.tapering_dose[currentIndex].dose || {}),
                    custom: option?.label,
                };
            }
        } else {
            if (clonedElement) {
                clonedElement.dose = {
                    ...(clonedElement.dose || {}),
                    custom: option?.label,
                };
            }
        }
        dispatch(upsertInjection(clonedElement, mainIndex));
    };
    const onAddTaperingDoseClick = () => {
        const { mainIndex } = injection;
        const clonedElements = [...elements];

        if (clonedElements[mainIndex].tapering_dose) {
            clonedElements[mainIndex].tapering_dose = [
                ...(clonedElements[mainIndex].tapering_dose || []),
                {} as InjectionsEntity,
            ];
        } else {
            clonedElements[mainIndex].tapering_dose = [{} as InjectionsEntity];
        }
        dispatch(setInjectionElements(clonedElements));
    };

    return isDragMode && (injection.is_added_drug || injection.is_tapering_dose) ? (
        <></>
    ) : (
        <div className="hover-parent-element">
            <DoseSearchComponent
                columnKey={columnKey}
                defaultValue={injection?.dose?.custom || ''}
                onDoseSelect={onDoseSelect}
                selectedValue={{
                    id: injection?.dose?.custom || '',
                    label: injection?.dose?.custom || '',
                    value: injection?.dose?.custom || '',
                    meta: {
                        unit: injection?.dose?.unit,
                        id: injection?.dose?.id,
                    },
                }}
                element={currentElement as InjectionsEntity}
            />
            {showAddTaperingStrip ? (
                <AddItemChip title="Taper Dose" onClick={onAddTaperingDoseClick} />
            ) : null}
        </div>
    );
};

export const DoseSearchComponent = ({
    columnKey,
    defaultValue,
    selectedValue,
    onDoseSelect,
    placeholder,
    allowEdit = true,
    element,
}: {
    columnKey: INJECTION_COLUMN;
    selectedValue: { id: string; label: string; value: string; meta: any };
    defaultValue: string;
    onDoseSelect: (
        option?: { id: string; label: string; value: string; meta: any } | undefined,
    ) => void;
    placeholder?: string;
    allowEdit?: boolean;
    element: InjectionsEntity;
}): JSX.Element => {
    const onDoseSearch = (
        value: string,
    ): Promise<{ id: string; label: string; value: string; meta: any }[]> => {
        if (!element) {
            return new Promise((resolve) => {
                resolve([]);
            });
        }

        const df_id = element.df_id;
        const dosage_form = element.dosage_form;

        const mxDoseFormsUnit = df_id ? MX_DOSAGE_FORM_UNIT[df_id] : undefined;

        let options: { label: string; value: string; meta: any }[] = [];

        if (!mxDoseFormsUnit) {
            const t =
                MX_DOSAGE_FORM_UNIT[FORM_TO_DF_MAPPING[dosage_form?.toLowerCase() || ''] || ''];

            if (t) {
                options = generateDoseOptions(t?.unit_list, t?.name)(value);
            } else {
                options = generateDoseOptions(
                    ALL_FORM_UNITS_FOR_MX,
                    '',
                )(value).filter((t) => t.label.toLowerCase().includes(value.toLowerCase()));
            }
        } else {
            options = generateDoseOptions(mxDoseFormsUnit.unit_list, mxDoseFormsUnit.name)(value);
        }

        return new Promise((resolve) => {
            resolve(options.map((t) => ({ ...t, id: t.label })));
        });
    };

    if (!allowEdit) {
        return (
            <AntAutoCompleteV2
                autoFocus
                onSearch={onDoseSearch}
                selectedValue={selectedValue}
                debounceDelay={200}
                getLabelToCompare={(o) => o.label}
                placeholder={placeholder || 'Dose'}
                customDropdownWidth={150}
                onSelect={onDoseSelect}
                key={`Injection-${columnKey}`}
                containerIdToAttachDropdown={INJECTIONS_TABLE_CONTAINER}
                customStyle={{
                    width: 100,
                }}
            />
        );
    }
    return (
        <EditableCellV2
            fieldName={columnKey}
            defaultValue={defaultValue}
            label={injectionRowHeaderMapping[columnKey]}
        >
            <AntAutoCompleteV2
                autoFocus
                onSearch={onDoseSearch}
                selectedValue={selectedValue}
                debounceDelay={200}
                getLabelToCompare={(o) => o.label}
                placeholder={placeholder || 'Dose'}
                customDropdownWidth={180}
                onSelect={onDoseSelect}
                key={`Injection-${columnKey}`}
                containerIdToAttachDropdown={INJECTIONS_TABLE_CONTAINER}
            />
        </EditableCellV2>
    );
};

export default InjectionDose;
